// cSpell:ignore betaling Onmiddellijke Stap Volgende Vorige
import { IPaymentPrefill } from "@rentiohq/shared-frontend/dist/forms/createPayment/schema.createPayment.types";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as paymentApi from "@rentiohq/shared-frontend/dist/redux/payment/payment.api";
import * as paymentUtils from "@rentiohq/shared-frontend/dist/redux/payment/payment.utils";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import { EContractFetchType } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import * as contractUtils from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.utils";
import { generalLedgerAccountModule } from "@rentiohq/shared-frontend/dist/reduxV2/generalLedgerAccount";
import * as paymentOrderActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.actions";
import * as paymentOrderSelectors from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.selectors";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.utils";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { EPaymentRequestFetchType } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  Can,
  Error,
  Loading,
  Page,
  RentioInternalRenderer,
} from "@rentiohq/web-shared/dist/components";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import * as t from "../../../../../../services/translationService";
import { CostPaymentRecap } from "./CostPaymentRecap";
import { getCostPaymentRecapRows } from "./costPaymentRecap.utils";
import { PaymentForm } from "./PaymentForm";
import { getOpenEndedPrefillContract } from "./prefillContract.utils";

interface IContainerProps {
  // When provided, render the flow inside a modal for this property instead of
  // reading the property from the route (used by the direct-cost payment popup).
  propertyIdOverride?: number;
  prefillOverride?: IPaymentPrefill;
  asModal?: boolean;
  // Render the form inline (no Page, no own Modal) so it can live as a step
  // inside another flow (e.g. the cost wizard's "Onmiddellijke betaling" step).
  inline?: boolean;
  // Embedded usage: parent provides a stable redux formId so it can subscribe
  // to the inner step state for a unified "Stap X / Y" counter.
  formId?: string | number;
  onSchemaCount?: (count: number) => void;
  // Imperative ref + first-step-back callback the parent uses to drive the
  // inner form's Volgende/Vorige from its single modal footer.
  formRef?: React.Ref<any>;
  onFirstStepPrev?: () => void;
  onStepChange?: (stepIndex: number) => void;
  onVisibleStepsChange?: (
    steps: { title: string; rawIndex: number }[],
    activeVisibleIndex: number,
  ) => void;
  onClose?: () => void;
  // Fires when the create request fails, so an embedding flow can recover
  // (e.g. link the orders a bulk submission created before it failed).
  onError?: (error: unknown) => void;
  // Runs before the form sends anything: false stops it, e.g. when what it
  // would create was created elsewhere in the meantime.
  beforeSubmit?: () => Promise<boolean>;
  // Shown above every step of the form, e.g. a warning of the embedding flow.
  intro?: React.ReactNode;
  // Leaves out the "Who pays what" recap above the summary of a cost
  // payment, for an embedding flow that says who pays what itself.
  hideCostRecap?: boolean;
}

const PaymentFormContainer: FC<IContainerProps> = ({
  propertyIdOverride,
  prefillOverride,
  asModal,
  inline,
  formId,
  onSchemaCount,
  formRef,
  onFirstStepPrev,
  onStepChange,
  onVisibleStepsChange,
  onClose,
  onError,
  beforeSubmit,
  intro,
  hideCostRecap = false,
}) => {
  // Router
  const params = useParams<{
    paymentOrderId: string;
    propertyId: string;
  }>();
  const paymentOrderId = params.paymentOrderId
    ? +params.paymentOrderId
    : undefined;
  const propertyId =
    propertyIdOverride ?? (params.propertyId ? +params.propertyId : undefined);

  // Optional prefill passed via navigation state (e.g. a direct cost payment).
  const location = useLocation();
  const prefill =
    prefillOverride ??
    ((location.state as any)?.prefill as IPaymentPrefill | undefined);

  // State
  const [isLoadingPaymentRequests, setIsLoadingPaymentRequests] =
    useState<boolean>(true);
  const [hasOpenPaymentRequests, setHasOpenPaymentRequests] =
    useState<boolean>(false);
  const [isLoadingPaymentOrder, setIsLoadingPaymentOrder] =
    useState<boolean>(true);
  const [paymentOrder, setPaymentOrder] = useState<IPaymentOrder>();
  // Whether the initial data load completed at least once — see the render
  // gate below (a background refetch must not unmount the form).
  const hasLoadedOnceRef = useRef(false);

  // Redux
  const dispatch = useDispatch();

  const { contactMeMaster } = authHooks.useSelf();

  const hasBookkeepingForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.Bookkeeping,
  );

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const activeContractsForPropertyFilter = propertyId
    ? contractUtils.getFilterForProperty(propertyId, EContractFetchType.Active)
    : undefined;
  const endedContractsForPropertyFilter = propertyId
    ? contractUtils.getFilterForProperty(propertyId, EContractFetchType.Ended)
    : undefined;

  const { items: activeContracts, isFetching: isLoadingContracts } =
    contractHooks.useGetAll({
      shouldRefetch: true,
      query: { filter: activeContractsForPropertyFilter },
    });
  const { items: endedContracts } = contractHooks.useGetAll({
    shouldRefetch: true,
    query: { filter: endedContractsForPropertyFilter },
  });

  // The lease a cost payment bills (prefill.contractId). An open-ended lease
  // (no end date) is in neither list, as "active" asks an end date after
  // today: it is read on its own, or the order lost its lease and the form
  // guessed the payer from another lease's rent.
  const prefillContractId = prefill?.costPayment
    ? prefill.contractId
    : undefined;
  const listedContracts = [
    ...(activeContracts ?? []),
    ...(endedContracts ?? []),
  ];
  const shouldReadPrefillContract =
    !!prefillContractId &&
    !!activeContracts &&
    !!endedContracts &&
    !listedContracts.some(contract => contract.id === prefillContractId);
  const {
    detail: prefillContract,
    isFetching: isFetchingPrefillContract,
    fetchError: prefillContractError,
  } = contractHooks.useDetail({
    id: shouldReadPrefillContract ? prefillContractId : undefined,
    shouldRefetch: false,
  });
  const openEndedPrefillContract = getOpenEndedPrefillContract({
    contract: prefillContract,
    propertyId,
    listedContracts,
  });
  // Until it is read, the form waits: it takes its lease once, when it opens.
  const isResolvingPrefillContract =
    shouldReadPrefillContract && !prefillContract && !prefillContractError;

  const {
    items: generalLedgerAccounts,
    isFetching: isLoadingGeneralLedgerAccounts,
  } = generalLedgerAccountModule.hooks.usePaged({
    shouldRefetch: true,
    query: hasBookkeepingForBroker
      ? {
          page: 1,
          limit: 100,
          filter: {
            isDefault: { is: false },
          },
          sort: [{ field: "code", method: "ASC" }],
        }
      : undefined,
  });

  const paymentOrdersQuery = {
    propertyId,
    filter: { completedAt: { is: null } },
    limit: 100,
  };
  const isLoadingPaymentOrders = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectors.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery(paymentOrdersQuery),
      )?.isFetching,
  );
  const paymentOrders = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectors.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery(paymentOrdersQuery),
      )?.items,
  );

  activeContracts?.sort(
    (a, b) =>
      new Date(a.startDate as Date).getTime() -
      new Date(b.startDate as Date).getTime(),
  );
  endedContracts?.sort(
    (a, b) =>
      new Date(a.startDate as Date).getTime() -
      new Date(b.startDate as Date).getTime(),
  );

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    dispatch(
      paymentOrderActions.getPagedStart.getAction({
        query: paymentOrderUtils.getPaymentOrdersQuery(paymentOrdersQuery),
      }),
    );
  }, [propertyId]);

  useEffect(() => {
    const fetchPaymentRequests = async () => {
      if (!paymentOrderId) {
        setIsLoadingPaymentRequests(false);
        return;
      }

      try {
        // Get open PR's for property
        const { data: openPaymentRequests } =
          await paymentApi.getPaymentRequests({
            skip: 0,
            filterData: {
              where: {
                propertyId,
                status: {
                  inq: paymentUtils.getPaymentRequestTypes(
                    EPaymentRequestFetchType.Open,
                  ),
                },
              },
            },
          });

        // Find PR with relevant PO
        const hasOpenPaymentRequests = !!openPaymentRequests.find(
          paymentRequest =>
            paymentRequest.paymentRequestItems.find(
              paymentRequestItem =>
                paymentRequestItem.paymentOrder.id === paymentOrderId,
            ),
        );

        setHasOpenPaymentRequests(hasOpenPaymentRequests);
      } catch (unknownError) {
        const error = unknownError as any;
        logger.logError({ error });
      }

      setIsLoadingPaymentRequests(false);
    };

    fetchPaymentRequests();
  }, [paymentOrderId, propertyId]);

  useEffect(() => {
    const fetchPaymentOrder = async () => {
      if (!paymentOrderId) {
        setIsLoadingPaymentOrder(false);
        return;
      }

      try {
        // Get open PR's for property
        const { data } = await paymentApi.getPaymentOrder(paymentOrderId);

        setPaymentOrder(data);
      } catch (unknownError) {
        const error = unknownError as any;
        logger.logError({ error });
      }

      setIsLoadingPaymentOrder(false);
    };

    fetchPaymentOrder();
  }, [paymentOrderId]);

  // Render
  const isLoading =
    isLoadingPaymentOrder ||
    isLoadingPaymentRequests ||
    isLoadingContracts ||
    isLoadingGeneralLedgerAccounts ||
    isLoadingPaymentOrders ||
    isFetchingPrefillContract ||
    isResolvingPrefillContract;
  const hasData =
    !!property && !!activeContracts && !!endedContracts && !!paymentOrders;
  // Only gate on <Loading/> for the INITIAL load. The contract/order hooks
  // refetch (`shouldRefetch: true`) while the form is open; swapping the tree
  // for a spinner then would unmount the MultiStepForm and reset the user to
  // the first step mid-flow. Once data is there, keep rendering while a
  // background refetch runs.
  if (!isLoading && hasData) {
    hasLoadedOnceRef.current = true;
  }

  if (!contactMeMaster) {
    return null;
  }

  if (isLoading && !hasLoadedOnceRef.current) {
    return <Loading />;
  }

  if (!property || !activeContracts || !endedContracts || !paymentOrders) {
    return <Error errors={[]} />;
  }

  const currentContracts = openEndedPrefillContract
    ? [...activeContracts, openEndedPrefillContract]
    : activeContracts;

  // A cost payment does not ask for the payer, the amount or the
  // description, so the summary would not show them: recap them above it.
  const memberAccounts = [
    ...property.members,
    ...currentContracts.flatMap(contract => contract.members),
    ...endedContracts.flatMap(contract => contract.members),
  ].map(member => member.account);
  const payerNameOf = (accountId?: number) => {
    const account = memberAccounts.find(({ id }) => id === accountId);
    return account ? getName(account) : undefined;
  };
  const recapRows =
    prefill && !hideCostRecap
      ? getCostPaymentRecapRows({ prefill, payerNameOf })
      : [];

  const renderContent = (forceInline = false) => (
    <PaymentForm
      property={property}
      paymentOrder={paymentOrder}
      hasOpenPaymentRequests={hasOpenPaymentRequests}
      shouldUpdateOpenPaymentRequests={true}
      activeContracts={currentContracts}
      endedContracts={endedContracts}
      generalLedgerAccounts={generalLedgerAccounts}
      paymentOrders={paymentOrders}
      prefill={prefill}
      asModal={forceInline ? false : asModal}
      noCard={forceInline}
      formId={formId}
      onSchemaCount={onSchemaCount}
      formRef={formRef}
      onFirstStepPrev={onFirstStepPrev}
      onStepChange={onStepChange}
      onVisibleStepsChange={onVisibleStepsChange}
      onClose={onClose}
      onError={onError}
      beforeSubmit={beforeSubmit}
      intro={intro}
      summaryIntro={
        recapRows.length > 0 ? <CostPaymentRecap rows={recapRows} /> : undefined
      }
    />
  );

  // Modal mode: render just the form-as-modal, without the page/breadcrumb chrome.
  if (asModal) {
    return renderContent();
  }

  // Inline mode: render the form inline (no Page) as a step inside another flow.
  if (inline) {
    return renderContent(true);
  }

  return (
    <Page
      title={paymentOrderId ? t.paymentsEditTitle() : t.paymentsCreateTitle()}
      subtitle={property && formatAddress(property, false)}
      metadata={<RentioInternalRenderer>V2</RentioInternalRenderer>}
      breadcrumbs={{
        to: `/properties/${propertyId}/payments`,
        content: t.paymentsDetailBreadcrumb(),
      }}
    >
      <Can
        role={property.roles}
        perform="propertyPayments:create"
        yes={renderContent}
        no={() => <Navigate to={`/properties/${propertyId}`} />}
      />
    </Page>
  );
};

// eslint-disable-next-line import/no-default-export
export default PaymentFormContainer;
